import React from 'react';
import { Button } from '../../../components/ui/Button2';
import { Icon } from '../../../components/ui/Icon';
import styles from './DocumentAssessment.module.css';

/**
 * How far the gap list below can be trusted.
 *
 * This sits in the assessment chrome, above the gaps, and is deliberately not a
 * tab: a customer who closes the gaps we found while an auditor finds the ones
 * we could not read is the worst outcome this tool can produce. When everything
 * was read it collapses to a quiet one-liner rather than shouting.
 */
const CoverageBanner = ({ coverage, onSeeDetails }) => {
  if (!coverage) return null;

  const total = coverage.documents_total ?? 0;
  const read = coverage.documents_read ?? 0;
  const complete = total > 0 && read === total;
  const unread = (coverage.documents_partial ?? 0) + (coverage.documents_unreadable ?? 0);

  if (total === 0) {
    return (
      <div className={styles.coverageWarn} role="status">
        <Icon name="warning" size="sm" tone="warning" />
        <span>No documents were supplied, so nothing could be assessed.</span>
      </div>
    );
  }

  if (complete) {
    return (
      <div className={styles.coverageOk} role="status">
        <Icon name="success" size="sm" tone="success" />
        <span>All {total} document{total === 1 ? '' : 's'} were read in full.</span>
      </div>
    );
  }

  const problems = (coverage.per_document || []).filter(
    (d) => d.state !== 'extracted' && d.reason,
  );

  return (
    <div className={styles.coverageWarn} role="status">
      <Icon name="warning" size="sm" tone="warning" />
      <div>
        <strong>
          Coverage: read {read} of {total} document{total === 1 ? '' : 's'} in full.
        </strong>
        {problems.map((d) => (
          <div className={styles.coverageDetail} key={d.document_id || d.document_name}>
            “{d.document_name}” — {d.reason}
          </div>
        ))}
        <div className={styles.coverageDetail}>
          {unread} document{unread === 1 ? '' : 's'} could not be fully read. Gaps
          below cover only what could be read.
        </div>
        {coverage.unchecked_work_products?.length > 0 && (
          /* Not gaps — we genuinely do not know whether these exist, because a
             document that might contain them was unreadable. Naming them turns
             a silent skip into an action: make the document readable, re-run. */
          <div className={styles.coverageDetail}>
            <strong>Could not be checked at all:</strong>{' '}
            {coverage.unchecked_work_products.join(', ')}. Re-run once these
            documents are readable.
          </div>
        )}
        {onSeeDetails && (
          <Button variant="link" size="sm" onClick={onSeeDetails}>
            See coverage details
          </Button>
        )}
      </div>
    </div>
  );
};

export default CoverageBanner;
